import React from "react";
import { useEffect } from "react";
import { BrowserRouter, Switch, Route } from "react-router-dom";
import { withCookies, Cookies } from "react-cookie";
import { useImmerReducer } from "use-immer";
import PropTypes from "prop-types";

import PrivateRoute from "./utils/PrivateRoute";
import DispatchContext from "./contexts/DispatchContext";
import StateContext from "./contexts/StateContext";

import FlashMessages from "./components/elements/FlashMessages";
import Sidebar from "./components/elements/Sidebar";

import SplashScreen from "./components/views/SplashScreen";
import Overview from "./components/views/Overview";

import Login from "./components/views/Login";
import ReqPwdReset from "./components/views/ReqPwdReset";
import PwdReset from "./components/views/PwdReset";

import Users from "./components/views/Users";
import CreateUser from "./components/views/CreateUser";
import User from "./components/views/User";

import Clients from "./components/views/Clients";
import CreateClient from "./components/views/CreateClient";
import Client from "./components/views/Client";

import Institutions from "./components/views/Institutions";
import CreateInstitution from "./components/views/CreateInstitution";
import Institution from "./components/views/Institution";

import AssignLicences from "./components/views/AssignLicences";
import GenerateLicences from "./components/views/GenerateLicences";
import ListLicences from "./components/views/ListLicences";

import Texts from "./components/views/Texts";
import Statistics from "./components/views/Statistics";
import Download from "./components/views/Download";
import SendRequest from "./components/views/SendRequest";

import Privacy from "./components/views/Privacy";
import Imprint from "./components/views/Imprint";
import Contact from "./components/views/Contact";

function App(props) {
  const { cookies } = props;
  const reducer = (draft, action) => {
    switch (action.type) {
      case "login":
        draft.loggedIn = true;
        draft.user = action.data;
        return;
      case "logout":
        draft.loggedIn = false;
        return;
      case "userdata":
        draft.user = action.data;
        return;
      case "update-client-licences":
        draft.user.client.licences = action.data;
        if (action.sail) draft.user.client.selfassignedInstitution.licences = action.sail;
        return;
      case "client-created":
        draft.user.clientCount++;
        return;
      case "institution-created":
        draft.user.client.institutions = action.client.institutions;
        return;
      case "notify":
        draft.flashMessages.push(action.msg);
        return;
      default:
        return null;
    }
  };
  const [state, dispatch] = useImmerReducer(reducer, {
    loggedIn: Boolean(cookies.get("tok")),
    flashMessages: [],
    user: {}
  });

  const getTitle = string => {
    let title = "KC21 Admin";
    if (string.toLowerCase() === "login") return title;
    let titles = {
      assignlicences: "Lizenzen versenden",
      client: "Mandantendetails",
      clients: "Mandantenübersicht",
      contact: "Kontakt",
      createclient: "Mandanten anlegen",
      createinstitution: "Institution anlegen",
      createuser: "Benutzer anlegen",
      download: "Downloads",
      generatelicences: "Lizenzen versenden",
      imprint: "Impressum",
      institution: "Institutionsdetails",
      institutions: "Institutionen",
      listlicences: "Lizenzenübersicht",
      overview: "Übersicht",
      privacy: "Datenschutz",
      pwdreset: "Passwort vergeben",
      reqpwdreset: "Passwort zurücksetzen",
      sendrequest: "Anfrage senden",
      statistics: "Statistik",
      texts: "Textverwaltung",
      user: "Benutzerdetails",
      users: "Benutzerübersicht"
    };
    title += Boolean(titles[string.toLowerCase()]) ? ` | ${titles[string.toLowerCase()]}` : "";
    return title;
  }

  useEffect(() => {
    let cookieOptions = {path: "/"};
    //if(process && process.env && process.env.REACT_APP_ADMIN_COOKIE_DOMAIN) cookieOptions.domain = process.env.REACT_APP_ADMIN_COOKIE_DOMAIN;
    if (!state.loggedIn) cookies.remove("tok", cookieOptions);
  }, [state.loggedIn, cookies]);

  return (
    <StateContext.Provider value={state}>
      <DispatchContext.Provider value={dispatch}>
        {state.loggedIn && !state.user.type ? (
          <SplashScreen token={state.user.token} />
        ) : (
          <BrowserRouter>
            <FlashMessages messages={state.flashMessages} />
            <Sidebar />
            <Switch>
              <Route path="/" exact>
                {state.loggedIn ? <Overview title={getTitle("Overview")} /> : <Login title={getTitle("login")} />}
              </Route>
              <Route path="/password/request" exact>
                <ReqPwdReset title={getTitle("ReqPwdReset")} />
              </Route>
              <Route path="/password/reset/:id" exact>
                <PwdReset title={getTitle("PwdReset")} />
              </Route>

              <PrivateRoute path="/client/create" exact>
                <CreateClient title={getTitle("CreateClient")} />
              </PrivateRoute>
              <PrivateRoute path="/client/list" exact>
                <Clients title={getTitle("Clients")} />
              </PrivateRoute>
              <PrivateRoute path="/client/:id" exact>
                <Client title={getTitle("Client")} />
              </PrivateRoute>

              <PrivateRoute path="/user/create" exact>
                <CreateUser title={getTitle("CreateUser")} />
              </PrivateRoute>
              <PrivateRoute path="/user/list" exact>
                <Users title={getTitle("Users")} />
              </PrivateRoute>
              <PrivateRoute path="/user/:id" exact>
                <User title={getTitle("User")} />
              </PrivateRoute>

              <PrivateRoute path="/institution/create" exact>
                <CreateInstitution title={getTitle("CreateInstitution")} />
              </PrivateRoute>
              <PrivateRoute path="/institution/list" exact>
                <Institutions title={getTitle("Institutions")} />
              </PrivateRoute>
              <PrivateRoute path="/institution/:id" exact>
                <Institution title={getTitle("Institution")} />
              </PrivateRoute>

              <PrivateRoute path="/licences/assign" exact>
                <AssignLicences title={getTitle("AssignLicences")} />
              </PrivateRoute>
              <PrivateRoute path="/licences/generate" exact>
                <GenerateLicences title={getTitle("GenerateLicences")} />
              </PrivateRoute>

              <PrivateRoute path="/testgroups/licences/list" exact>
                <ListLicences title={getTitle("ListLicences")} mode={"testgroups"} />
              </PrivateRoute>
              <PrivateRoute path="/testgroups/licences/generate" exact>
                <GenerateLicences title={getTitle("GenerateLicences")} mode={"testgroups"} />
              </PrivateRoute>

              <PrivateRoute path="/statistics" exact>
                <Statistics title={getTitle("Statistics")} />
              </PrivateRoute>
              <PrivateRoute path="/download" exact>
                <Download title={getTitle("Download")} />
              </PrivateRoute>

              <PrivateRoute path="/texts" exact>
                <Texts title={getTitle("Texts")} />
              </PrivateRoute>

              <PrivateRoute path="/sendrequest" exact>
                <SendRequest title={getTitle("SendRequest")} />
              </PrivateRoute>

              <Route path="/datenschutz" exact>
                <Privacy title={getTitle("Privacy")} />
              </Route>
              <Route path="/impressum" exact>
                <Imprint title={getTitle("Imprint")} />
              </Route>
              <Route path="/kontakt" exact>
                <Contact title={getTitle("Contact")} />
              </Route>
            </Switch>
          </BrowserRouter>
        )}
        {/* <pre>{JSON.stringify(state)}</pre> */}
      </DispatchContext.Provider>
    </StateContext.Provider>
  );
}

App.propTypes = {
  cookies: PropTypes.instanceOf(Cookies).isRequired
};

export default withCookies(App);
